import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ArrowRight, Lightning, Shield, GraduationCap, Wrench, FileText, Truck, Tag, Buildings } from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1759390304178-deb6bf827c95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwbWFjaGluZXJ5JTIwZGFya3xlbnwwfHx8fDE3ODE4NzgzMTZ8MA&ixlib=rb-4.1.0&q=85";

const benefits = [
  { icon: GraduationCap, title: "Training Center", desc: "Machine setup, troubleshooting, maintenance, compressor sizing, nozzle selection, and business development videos." },
  { icon: FileText, title: "Download Center", desc: "Estimate templates, rental agreements, safety docs, marketing materials, equipment guides, checklists." },
  { icon: Tag, title: "Member Pricing", desc: "Preferred pricing on machines, accessories, nozzles, air dryers, aftercoolers, and service parts." },
  { icon: Truck, title: "Early Access Inventory", desc: "5-day member-only access to used machines, demo units, refurbished equipment, and trade-in inventory." },
  { icon: Wrench, title: "Private Support", desc: "Direct line to Dry Ice Bulls. Submit tickets, attach images, get expert answers — fully private." },
  { icon: Buildings, title: "Future Network", desc: "Supplier directory, certified contractor program, lead distribution. Membership now secures your seat." },
];

export default function Landing() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#262626]" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40" />
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 slide-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#00D4FF]" />
              <span className="section-label" data-testid="hero-eyebrow">Private Member Portal — V1.0</span>
            </div>
            <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] uppercase text-white" data-testid="hero-headline">
              The Operating<br />System For Dry Ice<br /><span className="text-[#00D4FF]">Contractors.</span>
            </h1>
            <p className="mt-8 font-plex text-base sm:text-lg text-[#9CA3AF] max-w-2xl leading-relaxed" data-testid="hero-subhead">
              Dry Ice Connect is the private resource center for contractors, equipment owners, and businesses running dry ice blasting operations. Training, downloads, member pricing, early-access inventory — all in one place.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" className="btn-primary" data-testid="hero-cta-join">
                Join For $399/yr <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/login" className="btn-secondary" data-testid="hero-cta-login">
                Member Login
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 font-mono text-xs text-[#52525B] tracking-widest uppercase">
              <span>Annual Only</span>
              <span className="text-[#262626]">/</span>
              <span>Cancel Anytime</span>
              <span className="text-[#262626]">/</span>
              <span>Private & Secure</span>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex items-end">
            <div className="surface p-6 w-full">
              <div className="section-label mb-4">PRICING</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-chivo font-black text-6xl text-white">$399</span>
                <span className="font-mono text-sm text-[#52525B]">/YEAR</span>
              </div>
              <div className="border-t border-[#262626] pt-4 mt-4 space-y-2 text-sm font-plex text-[#9CA3AF]">
                <div className="flex items-center gap-2"><Lightning size={14} className="text-[#00D4FF]" weight="bold" /> Full training library</div>
                <div className="flex items-center gap-2"><Lightning size={14} className="text-[#00D4FF]" weight="bold" /> Download center access</div>
                <div className="flex items-center gap-2"><Lightning size={14} className="text-[#00D4FF]" weight="bold" /> Member equipment pricing</div>
                <div className="flex items-center gap-2"><Lightning size={14} className="text-[#00D4FF]" weight="bold" /> Early access inventory</div>
                <div className="flex items-center gap-2"><Lightning size={14} className="text-[#00D4FF]" weight="bold" /> Private support tickets</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-t border-[#262626] bg-[#0A0A0A] py-4 overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap">
            {["track-a", "track-b"].map((trackKey) => (
              <div key={trackKey} className="flex items-center gap-12 px-6 font-mono text-xs tracking-[0.3em] text-[#52525B] uppercase">
                <span>Machine Setup</span><span className="text-[#00D4FF]">●</span>
                <span>Compressor Sizing</span><span className="text-[#00D4FF]">●</span>
                <span>Nozzle Selection</span><span className="text-[#00D4FF]">●</span>
                <span>Air Treatment</span><span className="text-[#00D4FF]">●</span>
                <span>Quoting & Sales</span><span className="text-[#00D4FF]">●</span>
                <span>Troubleshooting</span><span className="text-[#00D4FF]">●</span>
                <span>Maintenance</span><span className="text-[#00D4FF]">●</span>
                <span>Business Development</span><span className="text-[#00D4FF]">●</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS BENTO */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-24" data-testid="benefits-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-5">
            <div className="section-label mb-4">— WHAT'S INSIDE</div>
            <h2 className="font-chivo font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none uppercase text-white">
              Everything you need.<br />Nothing you don't.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-8">
            <p className="font-plex text-base text-[#9CA3AF] leading-relaxed">
              Built for working contractors. No fluff, no community noise — only the resources, content, and pricing that move your business forward. Members never see other members. Conversations stay between you and Dry Ice Bulls.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
          {benefits.map((b) => (
            <div key={b.title} className="bg-[#0A0A0A] p-8 hover:bg-[#121212] transition-colors" data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <b.icon size={32} weight="duotone" className="text-[#00D4FF] mb-6" />
              <h3 className="font-chivo font-bold text-xl text-white mb-3">{b.title}</h3>
              <p className="font-plex text-sm text-[#9CA3AF] leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STATS / TRUST */}
      <section className="border-y border-[#262626] bg-[#121212]" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#262626]">
          {[
            { num: "100%", label: "Private. Members never see each other." },
            { num: "5-DAY", label: "Member-only inventory access window." },
            { num: "$399", label: "Annual flat fee. Cancel anytime." },
            { num: "24/7", label: "Ticket-based admin support." },
          ].map((s) => (
            <div key={s.num} className="px-6 first:pl-0">
              <div className="font-mono font-bold text-3xl lg:text-5xl text-[#00D4FF] tracking-tight">{s.num}</div>
              <div className="font-plex text-xs lg:text-sm text-[#9CA3AF] mt-2 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-24 text-center" data-testid="final-cta">
        <Shield size={48} weight="duotone" className="text-[#00D4FF] mx-auto mb-6" />
        <h2 className="font-chivo font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase text-white mb-4">
          Lock in your seat.
        </h2>
        <p className="font-plex text-base text-[#9CA3AF] max-w-xl mx-auto mb-10">
          The Certified Contractor Program and lead distribution system launch in Phase 3. Members today get first access.
        </p>
        <Link to="/register" className="btn-primary" data-testid="final-cta-join">
          Become A Member <ArrowRight size={18} weight="bold" />
        </Link>
      </section>
    </Layout>
  );
}
